import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { hero, stats, servicesOverview, approach, company } from "../mock";

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-60" />
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full">
          <Reveal>
            <p className="eyebrow mb-8">{hero.eyebrow}</p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display font-bold tracking-tight text-outline text-[2.6rem] sm:text-6xl lg:text-[5.2rem] leading-[1.02] max-w-5xl">
              {hero.title}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-8 text-stone-400 text-lg lg:text-xl leading-relaxed max-w-2xl">
              {hero.subtitle}
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link to={hero.primaryCta.path} className="btn-gold clip-corner px-7 py-3.5 text-sm font-semibold flex items-center justify-center gap-2">
                {hero.primaryCta.label} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to={hero.secondaryCta.path} className="btn-ghost clip-corner px-7 py-3.5 text-sm font-medium flex items-center justify-center gap-2">
                {hero.secondaryCta.label} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 divide-x divide-white/10">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <div className="py-12 px-4 lg:px-8">
                <p className="font-display text-3xl lg:text-5xl font-bold gold-text">{s.value}</p>
                <p className="mt-3 text-stone-400 text-xs lg:text-sm leading-snug">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-5">What we do</p>
            <h2 className="font-display text-4xl lg:text-6xl font-bold tracking-tight max-w-3xl leading-[1.05]">
              Three disciplines. <span className="gold-text">One balance sheet.</span>
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {servicesOverview.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.id} delay={i * 100}>
                  <Link to={s.path} className="svc-card block h-full border border-white/10 clip-corner p-8 group">
                    <div className="flex items-center justify-between mb-8">
                      <span className="font-mono-l text-xs text-stone-500 tracking-widest">{s.index}</span>
                      <Icon className="w-7 h-7 text-[#c8a951]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display text-2xl font-semibold mb-4 group-hover:text-[#c8a951] transition-colors">
                      {s.title}
                    </h3>
                    <p className="text-stone-400 text-sm leading-relaxed mb-8">{s.summary}</p>
                    <span className="inline-flex items-center gap-1.5 text-sm text-stone-300 group-hover:text-[#c8a951] transition-colors">
                      Learn more <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="py-28 border-t border-white/10 relative">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="eyebrow mb-5">Our approach</p>
                <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                  Built like an institution.
                </h2>
                <p className="mt-6 text-stone-400 leading-relaxed">
                  We bring structure, evidence, and patience to every decision — so your capital, coverage, and property work as one system.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-1 gap-px bg-white/10 border border-white/10">
              {approach.map((a, i) => {
                const Icon = a.icon;
                return (
                  <Reveal key={a.title} delay={i * 90}>
                    <div className="bg-[#08080a] p-8 flex gap-6 items-start hover:bg-[#0d0d10] transition-colors">
                      <Icon className="w-8 h-8 text-[#c8a951] shrink-0" strokeWidth={1.5} />
                      <div>
                        <h3 className="font-display text-xl font-semibold mb-2">{a.title}</h3>
                        <p className="text-stone-400 text-sm leading-relaxed">{a.body}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 border-t border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <p className="eyebrow mb-6">{company.tagline}</p>
            <h2 className="font-display text-4xl lg:text-[4rem] font-bold tracking-tight leading-[1.05] max-w-4xl mx-auto">
              Let’s build something that <span className="gold-text">endures.</span>
            </h2>
            <Link to="/contact" className="btn-gold clip-corner mt-10 inline-flex px-8 py-4 text-sm font-semibold items-center gap-2">
              Speak with our team <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
